<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'

import axios from 'axios'

import MarketingFlowPerformanceActionCard from '@/views/autopilot/Components/MarketingFlowPerformanceActionCard.vue'
import MarketingFlowPerformanceGlobalStats from '@/views/autopilot/Components/MarketingFlowPerformanceGlobalStats.vue'
import MarketingFlowPerformanceItems from '@/views/autopilot/Components/MarketingFlowPerformanceItems.vue'

import type { Statistics } from '@/views/autopilot/MarketingFlowPage.vue'
import type { CustomerDetails } from '@/types/types'

export interface MarketingFlowExecutionCustomerStat {
  customer_merchant: CustomerDetails
  last_triggered_at: string
  first_triggered_at: string
  triggered_count: number
}

const { t } = useI18n()
const { formatNumber, formatPercentage } = useNumber()
const { selectedStore } = useStore()

const { stores } = useLocalStorage()

const props = defineProps<{
  marketingFlowId: number
  hasRewardAction?: boolean
  hasEmailAction?: boolean
  hasSmsAction?: boolean
  hasPushAction?: boolean
}>()

const storeFilter = ref<number | null>(selectedStore.value)
const isFetching = ref(false)
const statistics = ref<Statistics | null>(null)
const users = ref<MarketingFlowExecutionCustomerStat[]>([])
const searchText = ref('')
const pagination = ref({
  currentPage: 1,
  lastPage: 1,
  total: 0,
  pageSize: 15
})
const isExecutionsLoading = ref(false)

const shouldShowRewardBlock = computed(() => {
  return props.hasRewardAction || (statistics.value?.count_reward_scan_conversions ?? 0) > 0
})

const emailStatistics = computed(() => {
  if (!props.hasEmailAction || !statistics.value) return { left: [], right: [] }

  const s = statistics.value
  const sent = s.email_sent ?? 0
  const delivered = s.email_delivered ?? 0

  const deliveredPct = sent > 0 ? (delivered / sent) * 100 : 0
  const openedPct = delivered > 0 ? ((s.email_opened_unique ?? 0) / delivered) * 100 : 0
  const clickedPct = delivered > 0 ? ((s.email_clicked ?? 0) / delivered) * 100 : 0
  const uniqueClickedPct = delivered > 0 ? ((s.email_clicked_unique ?? 0) / delivered) * 100 : 0
  const bouncedPct = sent > 0 ? ((s.email_bounced ?? 0) / sent) * 100 : 0
  const softBouncedPct = sent > 0 ? ((s.email_soft_bounced ?? 0) / sent) * 100 : 0
  const hardBouncedPct = sent > 0 ? ((s.email_hard_bounced ?? 0) / sent) * 100 : 0
  const spamPct = sent > 0 ? ((s.email_spam_complaints ?? 0) / sent) * 100 : 0

  return {
    left: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailSent'),
        value: formatNumber(sent, false),
        percentage: undefined,
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailSent')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailReceived'),
        value: formatNumber(delivered, false),
        percentage: formatPercentage(deliveredPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailReceived')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailOpened'),
        value: formatNumber(s.email_opened_unique ?? 0, false),
        percentage: formatPercentage(openedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailOpened')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailLinksClicked'),
        value: formatNumber(s.email_clicked ?? 0, false),
        percentage: formatPercentage(clickedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailLinksClicked')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailUniqueClicks'),
        value: formatNumber(s.email_clicked_unique ?? 0, false),
        percentage: formatPercentage(uniqueClickedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailUniqueClicks')
      }
    ],
    right: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailBounced'),
        value: formatNumber(s.email_bounced ?? 0, false),
        percentage: formatPercentage(bouncedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailBounced')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailSoftBounced'),
        value: formatNumber(s.email_soft_bounced ?? 0, false),
        percentage: formatPercentage(softBouncedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailSoftBounced')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailHardBounced'),
        value: formatNumber(s.email_hard_bounced ?? 0, false),
        percentage: formatPercentage(hardBouncedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailHardBounced')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailSpam'),
        value: formatNumber(s.email_spam_complaints ?? 0, false),
        percentage: formatPercentage(spamPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailSpam')
      }
    ]
  }
})

const smsStatistics = computed(() => {
  if (!props.hasSmsAction || !statistics.value) return { left: [], right: [] }

  const s = statistics.value
  const sent = s.sms_sent ?? 0
  const delivered = s.sms_delivered ?? 0
  const failed = s.sms_failed ?? 0
  const clicked = s.count_click_conversions ?? 0
  const clickedPct = s.click_conversion_percentage ?? 0

  const deliveredPct = sent > 0 ? (delivered / sent) * 100 : 0
  const failedPct = sent > 0 ? (failed / sent) * 100 : 0

  return {
    left: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.smsSent'),
        value: formatNumber(sent, false),
        percentage: undefined,
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.smsSent')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.smsDelivered'),
        value: formatNumber(delivered, false),
        percentage: formatPercentage(deliveredPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.smsDelivered')
      }
    ],
    right: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.smsFailed'),
        value: formatNumber(failed, false),
        percentage: formatPercentage(failedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.smsFailed')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.smsClicked'),
        value: formatNumber(clicked, false),
        percentage: clickedPct > 0 ? formatPercentage(clickedPct, 1) : undefined,
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.smsClicked')
      }
    ]
  }
})

const pushStatistics = computed(() => {
  if (!props.hasPushAction || !statistics.value) return { left: [], right: [] }

  const s = statistics.value
  const sent = s.count_messages ?? 0
  const clicked = s.count_click_conversions ?? 0
  const clickedPct = s.click_conversion_percentage ?? 0

  return {
    left: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.smsSent'),
        value: formatNumber(sent, false),
        percentage: undefined,
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.smsSent')
      }
    ],
    right: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.smsClicked'),
        value: formatNumber(clicked, false),
        percentage: clickedPct > 0 ? formatPercentage(clickedPct, 1) : undefined,
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.smsClicked')
      }
    ]
  }
})

// Note: pushStatistics reuses smsSent/smsClicked i18n keys intentionally
// as push and SMS share the same statistics model on the backend

const rewardStatistics = computed(() => {
  if (!shouldShowRewardBlock.value || !statistics.value) return { left: [], right: [] }

  const s = statistics.value
  const attributed = s.count_reward_scan_conversions ?? 0

  return {
    left: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.rewardUsed'),
        value: formatNumber(attributed, false),
        percentage: s.reward_scan_conversion_percentage
          ? formatPercentage(s.reward_scan_conversion_percentage, 1)
          : undefined,
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.rewardUsed')
      }
    ],
    right: []
  }
})

const setStoreFilter = async (storeId) => {
  if (storeId !== 0) {
    storeFilter.value = storeId
  } else {
    storeFilter.value = null
  }

  await fetchData()
}

const fetchStatistics = async () => {
  const params: {
    store_ids?: string
  } = {}

  if (storeFilter.value) params.store_ids = storeFilter.value?.toString()

  const { data } = await axios.get(`/marketing-flows/${props.marketingFlowId}/statistics`, {
    params
  })
  statistics.value = data.data
}
const fetchExecutions = async (page = 1) => {
  isExecutionsLoading.value = true
  const params: {
    store_ids?: string
    page?: number
    search?: string
  } = {
    page,
    search: searchText.value
  }

  if (storeFilter.value) params.store_ids = storeFilter.value?.toString()

  const { data } = await axios.get(`/marketing-flows/${props.marketingFlowId}/executions`, {
    params
  })

  users.value = data.data
  pagination.value.currentPage = data.meta.current_page
  pagination.value.lastPage = data.meta.last_page
  pagination.value.total = data.meta.total
  pagination.value.pageSize = data.meta.per_page
  isExecutionsLoading.value = false
}

const search = async (text: string) => {
  searchText.value = text
  pagination.value.currentPage = 1
  await fetchExecutions()
}
const searchCustomer = useDebounceFn(async (text) => await search(text), 500)

const fetchData = async () => {
  try {
    isFetching.value = true
    await Promise.all([fetchStatistics(), fetchExecutions()])
  } finally {
    isFetching.value = false
  }
}

onMounted(async () => {
  await fetchData()
})
</script>

<template>
  <div class="flex w-full flex-col gap-10">
    <!-- 1. Filter bar -->
    <div class="flex items-center">
      <div class="flex-1 flex flex-row items-center">
        <BaseIcon name="IconoirFilterList" class="h-6 w-6 text-foreground" />

        <div class="item-center flex-row px-3 text-foreground">
          <span>{{ t('global.filters.filter') }}</span>
        </div>

        <DropdownStore :stores-list="stores" @update-dropdown="setStoreFilter" />
      </div>

      <div class="flex items-center gap-2 text-blue-600">
        <BaseIcon name="HeroArrowPathIconSolid" class="h-5 w-5" />

        <span class="text-sm">
          {{ t('global.stats.upgradeAt') }}
        </span>
      </div>
    </div>

    <!-- 2. Performance globale du scénario -->
    <MarketingFlowPerformanceGlobalStats :statistics="statistics" />

    <!-- 3. Performance par action -->
    <div v-if="hasEmailAction || hasSmsAction || hasPushAction || shouldShowRewardBlock">
      <h2 class="mb-4 text-lg font-semibold text-foreground">
        {{ t('pages.marketingFlow.marketingFlowPerformance.performanceByAction') }}
      </h2>

      <div class="flex flex-col gap-5">
        <MarketingFlowPerformanceActionCard
          v-if="hasEmailAction"
          icon="HeroEnvelopeIconOutline"
          title="Email"
          :stats="emailStatistics"
        />

        <MarketingFlowPerformanceActionCard
          v-if="hasSmsAction"
          icon="IconoirMessage"
          title="SMS"
          :stats="smsStatistics"
        />

        <MarketingFlowPerformanceActionCard
          v-if="hasPushAction"
          icon="IconoirBell"
          title="PUSH"
          :stats="pushStatistics"
        />

        <MarketingFlowPerformanceActionCard
          v-if="shouldShowRewardBlock"
          icon="HeroGiftIconOutline"
          :title="t('pages.marketingFlow.marketingFlowPerformance.statistics.rewardUsedTitle')"
          :stats="rewardStatistics"
        />
      </div>
    </div>

    <!-- 4. Table Contacts touchés -->
    <MarketingFlowPerformanceItems
      :marketing-flow-id="marketingFlowId"
      :stats="users"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :page-size="pagination.pageSize"
      :is-fetching="isExecutionsLoading"
      @change-page="fetchExecutions"
      @search="searchCustomer"
    />
  </div>
</template>
